/**
 * Sokora Cart Gift & Milestone Engine (High-Performance Real-Time Sync)
 * Features:
 * 1. Zero-delay threshold evaluation (uses cart response directly, no extra /cart.js fetch)
 * 2. Bundled section rendering for instant drawer updates
 * 3. Auto-Add 1st Free Gift: Day & Night Face Sheet Mask (₹1 Variant: 67899973894376) when subtotal >= ₹999
 * 4. Auto-Add 2nd Free Gift: K-Golden Glaze Sheet Mask when subtotal >= ₹1499
 * 5. Auto-Removes individual gifts when subtotal drops below respective thresholds
 * 6. Eliminates cascading timeouts and circular re-render loops
 */
(function() {
  'use strict';

  var isSyncing = false;
  var syncDebounceTimer = null;
  var isInitialPageLoad = true;

  function getDrawer() {
    return document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
  }

  function isCartDrawerOpen() {
    if (window.location.pathname === (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart')) {
      return true;
    }
    var drawer = getDrawer();
    if (drawer) {
      if (drawer.hasAttribute('open') || drawer.isOpen) {
        return true;
      }
      var innerDrawer = drawer.querySelector('.wt-cart__drawer');
      if (innerDrawer && innerDrawer.classList.contains('wt-cart__drawer--open')) {
        return true;
      }
    }
    return false;
  }

  function getGiftConfig() {
    var drawer = getDrawer();
    var gift1VariantId = (drawer && drawer.getAttribute('data-free-gift-variant-id')) || '67899973894376';
    var gift2VariantId = (drawer && drawer.getAttribute('data-free-gift-variant-id-2')) || '';
    return {
      gift1VariantId: gift1VariantId,
      gift1Handle: (drawer && drawer.getAttribute('data-free-gift-handle')) || 'korean-day-nite-face-sheet-mask',
      gift2VariantId: gift2VariantId,
      gift2Handle: (drawer && drawer.getAttribute('data-free-gift-handle-2')) || '',
      shippingThreshold: parseInt((drawer && drawer.getAttribute('data-free-shipping-threshold')) || '20000', 10), // ₹200 in paise
      gift1Threshold: parseInt((drawer && drawer.getAttribute('data-free-gift-threshold')) || '99900', 10), // ₹999 in paise
      gift2Threshold: parseInt((drawer && drawer.getAttribute('data-free-gift-threshold-2')) || '99999999', 10)
    };
  }

  function identifyGiftItem(item, config) {
    if (!item) return null;

    // Check if property clearly marks which freebie it is
    if (item.properties && item.properties.Freebie) {
      var freebieText = String(item.properties.Freebie).toLowerCase();
      if (freebieText.indexOf('glaze') !== -1 || freebieText.indexOf('golden') !== -1) return 'gift2';
      if (freebieText.indexOf('day') !== -1 || freebieText.indexOf('night') !== -1) return 'gift1';
    }

    // Check Gift 1 by exact variant ID or known variant
    if (config.gift1VariantId && String(item.variant_id) === String(config.gift1VariantId)) {
      return 'gift1';
    }
    if (String(item.variant_id) === '67899973894376') {
      return 'gift1';
    }

    // Check Gift 2 by exact variant ID or known variant
    if (config.gift2VariantId && String(item.variant_id) === String(config.gift2VariantId)) {
      return 'gift2';
    }
    if (String(item.variant_id) === '67910322880744') {
      return 'gift2';
    }

    var handle = (item.handle || '').toLowerCase();
    var isGlazeHandle = handle.indexOf('glaze') !== -1 || handle.indexOf('k-golden-glaze') !== -1;
    var isDayNiteHandle = handle.indexOf('duo-face') !== -1 || handle.indexOf('day-nite') !== -1;

    if (item.properties && (item.properties._is_free_gift === 'true' || item.properties._is_free_gift === true || item.properties.Gift || item.properties.Freebie)) {
      if (isGlazeHandle) return 'gift2';
      return 'gift1';
    }

    if (item.variant_title && item.variant_title.toLowerCase().indexOf('free gift') !== -1) {
      if (isGlazeHandle) return 'gift2';
      return 'gift1';
    }

    if (item.final_price <= 100 && handle.indexOf('sheet-mask') !== -1) {
      if (isGlazeHandle) return 'gift2';
      return 'gift1';
    }

    return null;
  }

  function renderUpdatedDrawer(data) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (data && data.sections) {
      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents({ sections: data.sections }, false);
        return;
      }
    }

    if (typeof drawer.refreshCartDrawer === 'function') {
      drawer.refreshCartDrawer();
    }
  }

  function applyDrawerSections(sections) {
    if (!sections) return;
    var drawer = getDrawer();
    if (sections['cart-drawer']) {
      var tempDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      if (typeof syncCartDrawerCrossSell === 'function') {
        syncCartDrawerCrossSell(tempDoc, drawer, true);
      }
      renderUpdatedDrawer({ sections: sections });
    }
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = sections['cart-icon-bubble'];
        var newBubble = tempDiv.querySelector('.shopify-section') || tempDiv.firstElementChild;
        if (newBubble) bubble.innerHTML = newBubble.innerHTML;
      }
    }
    window.filterCrossSellSheetMasks?.();
  }

  async function checkAndSyncGift(existingCart) {
    if (isSyncing) return;
    var config = getGiftConfig();
    if (!config) return;

    isSyncing = true;
    try {
      var cart = existingCart;
      // If valid cart items array is not provided, fetch fresh /cart.js
      if (!cart || !Array.isArray(cart.items)) {
        var cartRes = await fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js');
        if (!cartRes.ok) return;
        cart = await cartRes.json();
      }

      var subtotalWithoutGift = 0;
      var gift1Items = [];
      var gift2Items = [];

      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var gType = identifyGiftItem(item, config);
        if (gType === 'gift1') {
          gift1Items.push(item);
        } else if (gType === 'gift2') {
          gift2Items.push(item);
        } else {
          subtotalWithoutGift += item.final_line_price;
        }
      }

      var drawer = getDrawer();
      if (drawer && cart.items && cart.items.length > 0) {
        drawer.classList.remove('is-empty', 'wt-cart--empty');
      }

      var needsGift1 = subtotalWithoutGift >= config.gift1Threshold; // >= ₹999
      var needsGift2 = false; // ₹1499 offer disabled for now

      // Celebrate reaching ₹999 milestone with confetti ONLY when viewing the cart and not on website landing
      if (needsGift1) {
        if (!sessionStorage.getItem('sokora_999_celebrated')) {
          if (!isInitialPageLoad && isCartDrawerOpen()) {
            sessionStorage.setItem('sokora_999_celebrated', 'true');
            setTimeout(function() {
              if (typeof window.triggerSokoraConfetti === 'function' && isCartDrawerOpen()) {
                window.triggerSokoraConfetti();
              }
            }, 350);
          } else {
            // Silently mark as celebrated on initial load / while cart is closed
            sessionStorage.setItem('sokora_999_celebrated', 'true');
          }
        }
      } else {
        sessionStorage.removeItem('sokora_999_celebrated');
      }

      var itemsToAdd = [];
      var updatesObj = {};
      var hasUpdates = false;

      // Strictly evaluate Gift 1
      if (needsGift1) {
        if (gift1Items.length === 0 && config.gift1VariantId) {
          itemsToAdd.push({
            id: parseInt(config.gift1VariantId, 10),
            quantity: 1,
            properties: {
              '_is_free_gift': 'true',
              'Freebie': 'Day & Night Sheet Mask (Free on ₹999+)'
            }
          });
        } else if (gift1Items.length > 0) {
          // Strictly enforce quantity === 1 for the primary gift item
          var primaryGift1 = gift1Items[0];
          if (primaryGift1.quantity !== 1) {
            updatesObj[primaryGift1.key] = 1;
            hasUpdates = true;
          }
          // Remove any duplicate gift 1 line items
          for (var d = 1; d < gift1Items.length; d++) {
            updatesObj[gift1Items[d].key] = 0;
            hasUpdates = true;
          }
        }
      } else {
        // Not eligible: remove all gift 1 items
        for (var d = 0; d < gift1Items.length; d++) {
          updatesObj[gift1Items[d].key] = 0;
          hasUpdates = true;
        }
      }

      // Strictly evaluate Gift 2 (offer currently disabled, purge all gift 2 items)
      for (var d2 = 0; d2 < gift2Items.length; d2++) {
        updatesObj[gift2Items[d2].key] = 0;
        hasUpdates = true;
      }

      // If nothing needs to be added, updated, or removed, exit cleanly
      if (itemsToAdd.length === 0 && !hasUpdates) {
        window.filterCrossSellSheetMasks?.();
        return;
      }

      var updatedSections = null;

      // 1. Perform Updates & Removals first via /cart/update.js
      if (hasUpdates) {
        var updateRes = await fetch((window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: updatesObj,
            sections: itemsToAdd.length === 0 ? 'cart-drawer,cart-icon-bubble' : undefined
          })
        });
        if (updateRes.ok) {
          var updateData = await updateRes.json();
          if (itemsToAdd.length === 0 && updateData.sections) {
            updatedSections = updateData.sections;
          }
        }
      }

      // 2. Perform Additions if needed via /cart/add.js
      if (itemsToAdd.length > 0) {
        var addRes = await fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: itemsToAdd,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });
        if (addRes.ok) {
          var addData = await addRes.json();
          if (addData.sections) {
            updatedSections = addData.sections;
          }
        }
      }

      if (updatedSections) {
        applyDrawerSections(updatedSections);
      } else {
        var drawerEl = document.querySelector('cart-drawer');
        if (drawerEl && typeof drawerEl.refreshCartDrawer === 'function') {
          drawerEl.refreshCartDrawer();
        }
      }
    } catch (e) {
      console.warn('[sokora-cart-gift-engine] Error syncing free gifts:', e);
    } finally {
      isSyncing = false;
    }
  }

  // Hook into fetch calls to detect cart mutations instantly without artificial delay
  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function() {
      var args = arguments;
      var url = args[0];
      var isCartMutation = typeof url === 'string' && (
        url.indexOf('/cart/add') !== -1 ||
        url.indexOf('/cart/change') !== -1 ||
        url.indexOf('/cart/update') !== -1 ||
        url.indexOf('/cart/clear') !== -1
      );

      var promise = originalFetch.apply(this, args);

      // Only listen to external caller mutations, ignore the engine's internal sync mutations
      if (isCartMutation && !isSyncing) {
        promise.then(function(response) {
          try {
            // Clone response so original caller can still read it without stream locking
            response.clone().json().then(function(data) {
              if (data && Array.isArray(data.items)) {
                // Instantly pass the already-fetched cart items! Zero delay!
                checkAndSyncGift(data);
              } else {
                clearTimeout(syncDebounceTimer);
                syncDebounceTimer = setTimeout(function() {
                  checkAndSyncGift();
                }, 30);
              }
            }).catch(function() {
              clearTimeout(syncDebounceTimer);
              syncDebounceTimer = setTimeout(function() {
                checkAndSyncGift();
              }, 30);
            });
          } catch(e) {}
        });
      }

      return promise;
    };
  }

  // Immediate check on initial page load (silent sync, never triggers confetti on website landing)
  function runInitialCheck() {
    setTimeout(function() {
      var checkPromise = checkAndSyncGift();
      if (checkPromise && typeof checkPromise.finally === 'function') {
        checkPromise.finally(function() {
          isInitialPageLoad = false;
        });
      } else {
        setTimeout(function() { isInitialPageLoad = false; }, 800);
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitialCheck);
  } else {
    runInitialCheck();
  }

  // Pre-Checkout Strict Cart Validation: Ensures no ineligible free gifts slip into GoKwik or Shopify Checkout
  async function validateAndCleanCartBeforeCheckout() {
    try {
      var maxWait = 2000;
      var start = Date.now();
      while (isSyncing && (Date.now() - start < maxWait)) {
        await new Promise(function(resolve) { setTimeout(resolve, 50); });
      }

      var config = getGiftConfig();
      if (!config) return true;

      // Always fetch fresh cart state directly from server
      var cartUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js?_t=' + Date.now();
      var cartRes = await fetch(cartUrl);
      if (!cartRes.ok) return true;
      var cart = await cartRes.json();
      if (!cart || !Array.isArray(cart.items)) return true;

      var subtotalWithoutGift = 0;
      var giftItemsToRemove = [];

      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var gType = identifyGiftItem(item, config);
        if (gType) {
          giftItemsToRemove.push(item);
        } else {
          subtotalWithoutGift += item.final_line_price;
        }
      }

      // If cart paid subtotal is below ₹999 and contains ANY free gifts, purge them immediately
      if (subtotalWithoutGift < config.gift1Threshold && giftItemsToRemove.length > 0) {
        var updatesObj = {};
        for (var j = 0; j < giftItemsToRemove.length; j++) {
          updatesObj[giftItemsToRemove[j].key] = 0;
        }

        var updateUrl = (window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js';
        var updateRes = await fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: updatesObj,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (updateRes.ok) {
          var updateData = await updateRes.json();
          if (updateData.sections) {
            applyDrawerSections(updateData.sections);
          }
        }
        return false;
      }

      return true;
    } catch (e) {
      console.warn('[sokora-cart-gift-engine] Error during pre-checkout validation:', e);
      return true;
    }
  }

  // Intercept GoKwik's onCheckoutClick to guarantee cart validation always executes
  function attachGokwikCheckoutInterceptor() {
    if (typeof window.onCheckoutClick === 'function' && !window.onCheckoutClick.__sokoraIntercepted) {
      var originalOnCheckoutClick = window.onCheckoutClick;
      var isVerifying = false;

      window.onCheckoutClick = function(btn) {
        if (isVerifying) return;
        isVerifying = true;

        validateAndCleanCartBeforeCheckout().then(function() {
          isVerifying = false;
          originalOnCheckoutClick(btn);
        }).catch(function() {
          isVerifying = false;
          originalOnCheckoutClick(btn);
        });
      };
      window.onCheckoutClick.__sokoraIntercepted = true;
    }
  }

  var gkCheckTimer = setInterval(function() {
    if (typeof window.onCheckoutClick === 'function') {
      attachGokwikCheckoutInterceptor();
      clearInterval(gkCheckTimer);
    }
  }, 200);
  setTimeout(function() { clearInterval(gkCheckTimer); }, 20000);

  window.sokoraCheckAndSyncGift = checkAndSyncGift;
  window.sokoraValidateAndCleanCartBeforeCheckout = validateAndCleanCartBeforeCheckout;
})();
